import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { DefaultAutocomplete } from "../../../../../../common/components/default_autocomplete";
import { FormTimeMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_time_masked_input";
import { ModalLoading } from "../../../../../../common/components/modal_loading";


import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import dayjs from "dayjs";

import { AcaoPrevCorretivaForm, AcaoPrevCorretivaSchema } from "../../../schemas/AcaoPrevCorretiva/qualidade_acoes_melhorias";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { DropDownFuncionario } from "../../../../../../common/components/dropdown_funcionario";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";


type Props = {
  open: boolean,
  saving: boolean,
  selectedAnalise: AcaoPrevCorretivaForm | null,
  onSubmit: (form: AcaoPrevCorretivaForm) => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onClose: () => void,
};

export const AnaliseCriticaEvidenciaModal = ({
  open,
  saving,
  selectedAnalise,
  onOpenChangeHistory,
  onSubmit,
  onClose,
}: Props) => {
  
  const hookForm = useForm<AcaoPrevCorretivaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(AcaoPrevCorretivaSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedAnalise) {
        let valorParaRadio = 2; // Padrão 'Pendente' (2)
      
        if (selectedAnalise.FoiEficaz === true) {
          valorParaRadio = 1; // Sim
        } else if (selectedAnalise.FoiEficaz === false) {
          valorParaRadio = 0; // Não
        }
        hookForm.reset({
          IdAcaoPrevCorretiva: selectedAnalise.IdAcaoPrevCorretiva,
          IdAcaoQualidade: selectedAnalise.IdAcaoQualidade,
          Tipo: 'P',
          DataAnalise: selectedAnalise.DataAnalise ? new Date(selectedAnalise.DataAnalise) : new Date(),
          IdFuncionarioAnalise: selectedAnalise.IdFuncionarioAnalise,
          AnaliseEvidencia: selectedAnalise.AnaliseEvidencia,
          FoiEficaz: valorParaRadio as any,
          
        });
      } else {
        hookForm.reset();
      }
    }
  }, [hookForm, open, selectedAnalise]);

  const handleSubmit = useCallback((form: AcaoPrevCorretivaForm) => {
    if (!saving){
      onSubmit(form);
      hookForm.reset(); 
    } 
  }, [hookForm, onSubmit, saving]);

  const handleClose = useCallback(() => {
    if (!saving){
      onClose();
      hookForm.reset(); 
    } 
  }, [hookForm, onClose, saving]);

  const handleChangeHistory = useCallback(() => {
    if (selectedAnalise) {
      onOpenChangeHistory(Number(selectedAnalise.IdAcaoPrevCorretiva), LogTipoTabela.ACAO_PREV_CORRETIVA);
    }
  }, [selectedAnalise, onOpenChangeHistory]);


  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "35rem",
          },
        },
      }}>
      <DialogTitle>
        Análise crítica e evidência objetiva das ações
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={selectedAnalise ? "Editar" : "Cadastrar"} arrangement="fullRow">
            <>
              <div className="flex-1 mt-4 mb-4">
                <FormRadioGroupInput
                  name='FoiEficaz'
                  label='A ação foi eficaz?'
                  options={[
                    { id: 0, label: 'Não' },
                    { id: 1, label: 'Sim' },
                    { id: 2, label: 'Análise Pendente' },
                  ]}
                  reactHookForm={hookForm} />
              </div>
              <DefaultFormInput
                name='AnaliseEvidencia'
                label="Análise e evidência objetiva"
                multiline={true}
                required={true}
                rows={4}
                reactHookForm={hookForm} />
                
                <DropDownFuncionario
                  name="IdFuncionarioAnalise"
                  readOnly={saving}
                  reactHookForm={hookForm}
                  label="Responsável pela análise"
                  required={true} />
                <FormDatePickerInput
                  name='DataAnalise'
                  label='Data Analise'
                  required={true}
                  reactHookForm={hookForm} />
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div className="w-[220px]">
            {
              isNotNullOrUndefined(selectedAnalise) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2 w-full justify-end">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}